import {AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";
import {MatTableDataSource} from "@angular/material/table";
import {CodeTableEnum} from "../../../shared/model/code";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatDialog} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {combineLatest, filter, map, skip, Subscription, take} from "rxjs";
import {selectSelectedGeneralPartnerFunds} from "../../store/general-partner/general-partner.selectors";
import {selectDealFundsManagementFunds} from "../../../deal/store/deal/deal.selectors";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {FundListActions} from "../../store/fund.actions";
import {Fund} from "../../models/fund";
import {TraceableMoney} from "../../../shared/model/traceable";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../../shared/user-setting/store/user-setting.actions";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {TableSort} from "../../../shared/generated-sources/api/user-setting/model/table-sort";
import {FUND_ALL_COLUMNS, FUND_COLUMN_GROUPS, FUND_COLUMN_LABELS, FUND_TABLE_DEFAULT_COLUMNS, FUND_TABLE_SORTABLE_COLUMNS} from "./fund-table-columns";

export type FundTableParentComponentEnum = "generalPartner" | "dealFundsManagement";

@Component({
    selector: "valumize-fund-table",
    templateUrl: "./fund-table.component.html",
    styleUrls: ["./fund-table.component.scss"],
    standalone: false
})
export class FundTableComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);

    @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild("fundSort") fundSort!: MatSort;
    @ViewChild("fundPaging") fundPaging!: MatPaginator;

    @Input({required: true}) parentComponent!: FundTableParentComponentEnum;
    @Input() showArchivedFilter: boolean = false;
    // When set, the table gains the column picker + persisted columns/sort/pageSize (keyed by this
    // value via the user-setting store). When absent the table behaves as before (no settings).
    @Input() tableKey?: string;
    @Output() fundClick = new EventEmitter<{ id: number; name: string; size: TraceableMoney }>();

    subscriptions: Subscription[] = [];

    fundColumns = [...FUND_TABLE_DEFAULT_COLUMNS];
    fundDataSource = new MatTableDataSource<Fund>();
    searchVisible = false;

    pageSizeOptions: number[] = [10, 20, 30];
    pageSize = 20;
    private activeSort: TableSort = {field: "fundName", direction: "asc"};

    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY

    showArchived: boolean = false;

    constructor() {
    }

    ngOnInit() {
        const selector = this.getFundsSelector();
        this.subscriptions.push(
            this.store.select(selector).pipe(map(funds =>
                this.fundDataSource.data = funds
            )).subscribe());

        if (this.tableKey) {
            this.pageSizeOptions = [10, 20, 30, 50, 100];
            this.subscriptions.push(
                this.store.select(selectSettingsLoaded).pipe(
                    filter(Boolean),
                    take(1)
                ).subscribe(() => this.applySettingsFromStore()));
            this.subscriptions.push(
                this.store.select(selectTableColumns(this.tableKey)).pipe(
                    skip(1),
                    filter((cols): cols is string[] => !!cols)
                ).subscribe(cols => {
                    const valid = this.filterValidColumns(cols);
                    if (valid) this.fundColumns = valid;
                }));
        }
    }

    ngAfterViewInit(): void {
        this.fundDataSource.paginator = this.fundPaging;
        this.fundDataSource.sort = this.fundSort;

        this.fundDataSource.filterPredicate = (data, filterText) => {
            const name = data.name?.text?.toLowerCase() ?? "";
            const transformedFilter = filterText.trim().toLowerCase();
            return name.includes(transformedFilter) ?? false;
        };
        this.fundDataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case "fundId":
                    return item.id ?? 0;
                case "fundName":
                    return item.name.text;
                case "country":
                    return item.country.code;
                case "currencyIso":
                    return item.currencyIso.code;
                case "size":
                    return item.size.amount;
                case "mainFundSize":
                    return item.mainFundSize.amount;
                case "vintageYear":
                    return item.vintageYear.date;
                case "endYear":
                    return item.endYear.date;
                default:
                    return (item as any)[property];
            }
        };

        if (this.tableKey) {
            this.fundDataSource.paginator.pageSize = this.pageSize;
        }

        Promise.resolve().then(() => this.applySort(this.activeSort));
    }

    private applySort(sort: TableSort) {
        this.fundDataSource.sort?.sort({
            id: sort.field,
            start: sort.direction,
            disableClear: true,
        });
    }

    onArchivedChanged(event: MatCheckboxChange) {
        this.showArchived = event.checked;
        this.store.dispatch(FundListActions.load({archived: this.showArchived}));
    }

    getFundsSelector() {
        switch (this.parentComponent) {
            case "generalPartner":
                return selectSelectedGeneralPartnerFunds;
            case "dealFundsManagement":
                return selectDealFundsManagementFunds;
        }
    }

    openTableSettings() {
        if (!this.tableKey) {
            return;
        }
        const dialogData: TableSettingsDialogData = {
            tableKey: this.tableKey,
            columnGroups: FUND_COLUMN_GROUPS,
            columnLabels: FUND_COLUMN_LABELS,
            currentColumns: this.fundColumns,
            currentPageSize: this.pageSize,
            currentSort: this.activeSort,
            pageSizeOptions: this.pageSizeOptions,
            sortableColumns: FUND_TABLE_SORTABLE_COLUMNS,
            isAdmin: false,
            defaultColumns: FUND_TABLE_DEFAULT_COLUMNS,
            defaultPageSize: 20,
            defaultSort: {field: "fundName", direction: "asc"},
        };

        this.subscriptions.push(
            this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
                .afterClosed()
                .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result)));
    }

    private applySettingsFromStore() {
        if (!this.tableKey) {
            return;
        }
        combineLatest([
            this.store.select(selectTableColumns(this.tableKey)),
            this.store.select(selectTablePageSize(this.tableKey)),
            this.store.select(selectTableSort(this.tableKey))
        ]).pipe(take(1)).subscribe(([columns, pageSize, sort]) => {
            if (columns) {
                const valid = this.filterValidColumns(columns);
                if (valid) this.fundColumns = valid;
            }
            if (pageSize) {
                this.pageSize = pageSize;
                if (this.fundDataSource.paginator) this.fundDataSource.paginator.pageSize = pageSize;
            }
            if (sort) {
                this.activeSort = sort;
                this.applySort(sort);
            }
        });
    }

    private filterValidColumns(columns: string[]): string[] | null {
        const valid = columns.filter(column => FUND_ALL_COLUMNS.includes(column));
        return valid.length > 0 ? valid : null;
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result || !this.tableKey) {
            return;
        }
        this.fundColumns = result.columns;
        this.activeSort = result.sort;
        this.applySort(result.sort);
        this.pageSize = result.pageSize;
        if (this.fundDataSource.paginator) this.fundDataSource.paginator.pageSize = result.pageSize;

        const payload = {tableKey: this.tableKey, columns: result.columns, pageSize: result.pageSize, sort: result.sort};
        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }

    emitFundClick(id: number, name: string, size: TraceableMoney) {
        this.fundClick.emit({id, name, size});
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.fundDataSource.filter = filterValue.trim().toLowerCase();
    }

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        if (this.searchVisible) {
            setTimeout(() => this.searchInput?.nativeElement.focus());
        } else {
            this.fundDataSource.filter = "";
        }
    }

    ngOnDestroy = () => this.subscriptions.forEach(subscription => subscription.unsubscribe());
}
